import { classicyLog } from "@/SystemFolder/SystemResources/Log/ClassicyLog";
/**
 * HyperCard.app — a JSON-driven HyperCard stack player for Classicy.
 *
 * Renders the active stack's current card in a window, wires the Go/File menus,
 * and bridges the pure interpreter to the outside world: it consumes queued
 * sound/openApp effects, drives `wait` timers, animates card transitions, and
 * shows ask/answer dialogs — all by dispatching back into the reducer.
 */

import {
	type FC as FunctionalComponent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	useAppManager,
	useAppManagerDispatch,
} from "@/SystemFolder/ControlPanels/AppManager/ClassicyAppManagerUtils";
import { useSoundDispatch } from "@/SystemFolder/ControlPanels/SoundManager/ClassicySoundManagerContext";
import { HyperCardEditorCanvas } from "@/SystemFolder/HyperCard/Editor/HyperCardEditorCanvas";
import {
	registerDownloadSaveProvider,
	serializeStack,
} from "@/SystemFolder/HyperCard/Editor/HyperCardEditorSave";
import type {
	HCEditState,
	HyperCardEditorData,
} from "@/SystemFolder/HyperCard/Editor/HyperCardEditorUtils";
import { HyperCardInspector } from "@/SystemFolder/HyperCard/Editor/HyperCardInspector";
import { HyperCardScriptEditor } from "@/SystemFolder/HyperCard/Editor/HyperCardScriptEditor";
import { HyperCardToolsPalette } from "@/SystemFolder/HyperCard/Editor/HyperCardToolsPalette";
import { HyperCardCard } from "@/SystemFolder/HyperCard/HyperCardCard";
import { HyperCardDialog } from "@/SystemFolder/HyperCard/HyperCardDialog";
import {
	type HCStack,
	validateStack,
} from "@/SystemFolder/HyperCard/HyperCardModel";
import {
	getHyperCardEffectHandler,
	getHyperCardSaveProviders,
	getRegisteredStacks,
} from "@/SystemFolder/HyperCard/HyperCardPlugins";
import { evaluateScriptEffect } from "@/SystemFolder/HyperCard/HyperCardScriptEffects";
import { HyperCardTransition } from "@/SystemFolder/HyperCard/HyperCardTransition";
import {
	getCard,
	type HCOpenStack,
	HyperCardAppInfo,
	type HyperCardData,
	isHyperCardData,
} from "@/SystemFolder/HyperCard/HyperCardUtils";
import { ClassicyAlert } from "@/SystemFolder/SystemResources/Alert/ClassicyAlert";
import { ClassicyApp } from "@/SystemFolder/SystemResources/App/ClassicyApp";
import { useClassicyAboutMenu } from "@/SystemFolder/SystemResources/App/ClassicyAppMenuHooks";
import {
	quitAppHelper,
	quitMenuItemHelper,
} from "@/SystemFolder/SystemResources/App/ClassicyAppUtils";
import { ClassicyControlLabel } from "@/SystemFolder/SystemResources/ControlLabel/ClassicyControlLabel";
import { resolveFileSystemEntrySource } from "@/SystemFolder/SystemResources/File/ClassicyFileSystemContentResolver";
import { useClassicyFileSystem } from "@/SystemFolder/SystemResources/File/ClassicyFileSystemContext";
import { ClassicyFileSystemEntryFileType } from "@/SystemFolder/SystemResources/File/ClassicyFileSystemModel";
import { desktopVolume } from "@/SystemFolder/SystemResources/FileDialog/ClassicyFileDialogVolume";
import { ClassicyFileOpenDialog } from "@/SystemFolder/SystemResources/FileDialog/ClassicyFileOpenDialog";
import { ClassicyFileSaveDialog } from "@/SystemFolder/SystemResources/FileDialog/ClassicyFileSaveDialog";
import type { ClassicyMenuItem } from "@/SystemFolder/SystemResources/Menu/ClassicyMenu";
import { decompressFromBase64 } from "@/SystemFolder/SystemResources/Utils/base64Compression";
import { ClassicyWindow } from "@/SystemFolder/SystemResources/Window/ClassicyWindow";
// Side-effect import: registers the ClassicyAppHyperCard* reducer.
import "./HyperCardContext";
// Side-effect import: registers the ClassicyAppHCEdit* reducer.
import "./Editor/HyperCardEditorContext";
import "./HyperCard.scss";

// Registers the built-in Download save provider under the save-provider registry.
registerDownloadSaveProvider();

const { id: appId, name: appName, icon: appIcon } = HyperCardAppInfo;

function useHyperCardData(): HyperCardData | undefined {
	return useAppManager((s) => {
		const raw = s.System.Manager.Applications.apps[appId]?.data;
		return raw && isHyperCardData(raw) ? raw : undefined;
	});
}

/**
 * A fresh id for a File → New Stack… stack, unique against currently-open
 * stacks. `Date.now()` collisions are effectively impossible in practice, but
 * a numeric suffix keeps the id stable if one ever occurs (e.g. two rapid
 * clicks in a test with a mocked clock).
 */
function generateUntitledStackId(
	openStacks: Record<string, unknown> | undefined,
): string {
	const base = `untitled-${Date.now()}`;
	if (!openStacks?.[base]) return base;
	let suffix = 2;
	while (openStacks[`${base}-${suffix}`]) suffix += 1;
	return `${base}-${suffix}`;
}

/** A blank stack seeded for File → New Stack… — one empty card, no parts. */
function makeBlankStack(): HCStack {
	return {
		name: "Untitled Stack",
		version: "2",
		size: [420, 300],
		cards: [{ id: "card1" }],
	};
}

export const HyperCard: FunctionalComponent = () => {
	const dispatch = useAppManagerDispatch();
	const player = useSoundDispatch();
	const { aboutMenuItem, aboutWindow } = useClassicyAboutMenu(
		appId,
		appName,
		appIcon,
	);

	const [savedStacksOpen, setSavedStacksOpen] = useState(false);
	const [saveStackOpen, setSaveStackOpen] = useState(false);

	const data = useHyperCardData();
	const activeStackId = data?.activeStackId;
	const open: HCOpenStack | undefined = activeStackId
		? data?.openStacks[activeStackId]
		: undefined;

	const edit: HCEditState | undefined = useAppManager((s) => {
		const d = s.System.Manager.Applications.apps[appId]?.data as
			| HyperCardEditorData
			| undefined;
		return activeStackId ? d?.edits?.[activeStackId] : undefined;
	});
	const editingActive = Boolean(edit) && edit?.tool !== "browse";

	// The palettes mount on `edit && activeStackId` (tool-independent — they
	// stay up during browse-preview), so the View menu tracks that, not
	// `editingActive`, which excludes the browse tool.
	const palettesMounted = Boolean(edit && activeStackId);

	// The two utility palettes' live visibility. `closed` is the store's single
	// source of truth (their title-bar close boxes flip it), so the View menu's
	// checkmarks stay in sync no matter how a palette was hidden.
	const toolsClosed = useAppManager(
		(s) =>
			s.System.Manager.Applications.apps[appId]?.windows.find(
				(w) => w.id === "hypercard_tools",
			)?.closed ?? false,
	);
	const infoClosed = useAppManager(
		(s) =>
			s.System.Manager.Applications.apps[appId]?.windows.find(
				(w) => w.id === "hypercard_inspector",
			)?.closed ?? false,
	);

	// #236: unsaved-changes guard. A single alert (Save/Discard/Cancel) is
	// shared by the single-window close path (onBeforeClose below) and the
	// Quit cascade (handleQuit below) — both just await confirmDirtyStack for
	// whichever stack needs confirming and never show more than one alert at
	// once. `closeGuardResolveRef` carries the in-flight promise's resolver
	// across renders since a click on one of the alert's buttons happens in a
	// later render than the one that created the promise.
	const [closeGuardStackId, setCloseGuardStackId] = useState<string | null>(
		null,
	);
	const closeGuardResolveRef = useRef<((proceed: boolean) => void) | null>(
		null,
	);
	const closeGuardEdit = useAppManager((s) => {
		const d = s.System.Manager.Applications.apps[appId]?.data as
			| HyperCardEditorData
			| undefined;
		return closeGuardStackId ? d?.edits?.[closeGuardStackId] : undefined;
	});

	const confirmDirtyStack = useCallback((stackId: string): Promise<boolean> => {
		return new Promise<boolean>((resolve) => {
			closeGuardResolveRef.current = resolve;
			setCloseGuardStackId(stackId);
		});
	}, []);

	const resolveCloseGuard = useCallback((proceed: boolean) => {
		const resolve = closeGuardResolveRef.current;
		closeGuardResolveRef.current = null;
		setCloseGuardStackId(null);
		resolve?.(proceed);
	}, []);

	// The alert's Save button. Reads the edit session fresh (not the `edit`
	// closed over from render) because the Quit cascade below can reach this
	// for a stack that isn't the active one, and because this same closure is
	// reused across every iteration of that cascade's loop. Mirrors the
	// provider.save flow the File menu's "Save a Copy…"/"Save to X" items use
	// (see the File menu below) so a failed save behaves identically: an
	// OpenFileFailed dispatch and the close/quit is aborted rather than
	// silently discarding the user's edits.
	const saveDirtyStack = useCallback(
		(stackId: string) => {
			const freshEdit = (
				useAppManager.getState().System.Manager.Applications.apps[appId]
					?.data as HyperCardEditorData | undefined
			)?.edits?.[stackId];
			if (!freshEdit) {
				resolveCloseGuard(true);
				return;
			}
			// The most-recently-registered save-capable provider: the built-in
			// "download" provider registers first (at this module's load), so a
			// host that registers its own provider (e.g. a real cloud save) takes
			// priority over the generic browser download for this single Save
			// button — unlike the File menu below, which lists every provider
			// separately and lets the user pick.
			const canSaveProviders = getHyperCardSaveProviders().filter((p) =>
				p.canSave(),
			);
			const provider = canSaveProviders[canSaveProviders.length - 1];
			if (!provider) {
				dispatch({ type: "ClassicyAppHCEditMarkSaved", stackId });
				resolveCloseGuard(true);
				return;
			}
			void provider
				.save(freshEdit.draft, { stackId })
				.then((result) => {
					if ("error" in result) {
						dispatch({
							type: "ClassicyAppHyperCardOpenFileFailed",
							path: "",
							message: `The stack can’t be saved: ${result.error}`,
						});
						resolveCloseGuard(false);
						return;
					}
					dispatch({ type: "ClassicyAppHCEditMarkSaved", stackId });
					if (result.ref) {
						const newStackId = `saved:${provider.id}:${result.ref.id}`;
						if (newStackId !== stackId) {
							dispatch({
								type: "ClassicyAppHCEditRebindStack",
								stackId,
								newStackId,
							});
						}
					}
					resolveCloseGuard(true);
				})
				.catch((err: unknown) => {
					dispatch({
						type: "ClassicyAppHyperCardOpenFileFailed",
						path: "",
						message: `The stack can’t be saved: ${err instanceof Error ? err.message : String(err)}`,
					});
					resolveCloseGuard(false);
				});
		},
		[dispatch, resolveCloseGuard],
	);

	// Single-window close: the main window's own close box/Escape funnel
	// through ClassicyWindow's onBeforeClose. Only the active stack is in
	// scope here — the Quit cascade below is what walks every open stack.
	const onBeforeCloseMain = useCallback((): boolean | Promise<boolean> => {
		if (!activeStackId || !edit?.dirty) return true;
		return confirmDirtyStack(activeStackId);
	}, [activeStackId, edit?.dirty, confirmDirtyStack]);

	// Quit: walks every dirty edit session one at a time (stable insertion
	// order of the `edits` map — HyperCard has a single visible document
	// window, so there's no window stacking order to key off; the order
	// stacks were opened/entered edit mode in is the closest analog). Cancel
	// on any one alert aborts the whole quit immediately; Save/Discard
	// resolves that stack and the loop re-reads the freshest state to find
	// the next remaining dirty stack, repeating until none are left.
	const handleQuit = useCallback(() => {
		const runCascade = async (): Promise<void> => {
			for (;;) {
				const freshData = useAppManager.getState().System.Manager.Applications
					.apps[appId]?.data as HyperCardEditorData | undefined;
				const dirtyStackId = Object.keys(freshData?.edits ?? {}).find(
					(id) => freshData?.edits?.[id]?.dirty,
				);
				if (!dirtyStackId) break;
				const proceed = await confirmDirtyStack(dirtyStackId);
				if (!proceed) return;
			}
			dispatch(quitAppHelper(appId, appName, appIcon));
		};
		void runCascade();
	}, [confirmDirtyStack, dispatch]);

	// Browse-preview: entering the browse tool pushes the draft into the player.
	const editTool = edit?.tool;
	useEffect(() => {
		if (!activeStackId || !edit || editTool !== "browse") return;
		dispatch({
			type: "ClassicyAppHyperCardOpenStack",
			stackId: activeStackId,
			stack: edit.draft,
		});
	}, [activeStackId, edit, editTool, dispatch]);

	const runtime = open?.runtime;
	const pendingEffects = runtime?.pendingEffects;
	const waitToken = runtime?.wait?.token;
	const waitMs = runtime?.wait?.ms;

	// --- consume queued sound / openApp effects (by id, once) ---
	const playedRef = useRef<Set<number>>(new Set());
	useEffect(() => {
		if (
			!open ||
			!activeStackId ||
			!pendingEffects ||
			pendingEffects.length === 0
		)
			return;
		const consumed: number[] = [];
		for (const e of pendingEffects) {
			consumed.push(e.id);
			if (playedRef.current.has(e.id)) continue;
			playedRef.current.add(e.id);
			if (e.kind === "beep") {
				// PlayInterrupt, not Play: ClassicySoundPlay is gated on total
				// silence, and the button's own ClassicyButtonClickUp is still
				// sounding when the script's beep lands in the same tick — so a
				// plain Play is silently dropped (#220). Interrupt stops all
				// audio first, which is the right call for script-driven sound:
				// the most recently requested sound is the intended one.
				player({ type: "ClassicySoundPlayInterrupt", sound: "ClassicyBeep" });
			} else if (e.kind === "play") {
				player({ type: "ClassicySoundPlayInterrupt", sound: e.sound });
			} else if (e.kind === "openApp") {
				// ClassicyAppOpen's predicate requires { id, name, icon }; stacks
				// author only the app id, so resolve the registered app record.
				const target =
					useAppManager.getState().System.Manager.Applications.apps[e.appId];
				// `e.event` is stack-authored JSON (validateStack checks shape, not
				// authority), so it can name literally any action type, not just
				// ClassicyAppOpen — this dispatch is UNTRUSTED content. It must clear
				// evaluateScriptEffect's two ordered checks: (1) when the owning app
				// declared a `params` schema for this action in its manifest, the
				// script-authored args must pass it — a malformed-but-allowed call is
				// dropped with a dev warning naming the bad param instead of
				// dispatching a doomed action; (2) the deny-by-default allowlist
				// (only "ClassicyAppOpen" passes with zero host configuration). On
				// either failure the effect is dropped entirely; otherwise dispatch
				// as "untrusted" so the reducer's kernel floor
				// (isActionTrustPermitted) also applies underneath as
				// defense-in-depth, per the pattern in ClassicyActionTrust.ts.
				const actionType = e.event ?? "ClassicyAppOpen";
				const decision = evaluateScriptEffect(actionType, e.data ?? {});
				if (decision.kind === "dispatch") {
					// Dispatch the SANITIZED args (zod's parsed output when a schema
					// exists), and pin `type`/`app` AFTER the spread so script-authored
					// keys can never override the gated action type or the
					// host-resolved app record.
					dispatch(
						{
							...decision.args,
							type: actionType,
							app: target
								? { id: target.id, name: target.name, icon: target.icon }
								: { id: e.appId },
						},
						"untrusted",
					);
				}
			} else if (e.kind === "custom") {
				// The three dispatches below (ClassicyAppHyperCardResolveCommand,
				// ClassicyAppHyperCardCommitField, ClassicyAppHyperCardSetVariable)
				// stay at the default TRUSTED level, deliberately, unlike the
				// openApp branch above. The distinction is what the untrusted gate
				// actually protects: the dispatched action's `type` (the route), not
				// its payload. Here `type` is always one of these three hardcoded
				// string literals written in this file — it is never derived from
				// `e.name` or any other stack-authored string — so there is no
				// script-controlled route for an untrusted gate to police. `e.name`
				// only selects which host-registered handler (getHyperCardEffectHandler)
				// runs; the resolve/setField/setVariable callbacks below are that
				// handler's own continuation mechanism back into the engine, not
				// stack script reaching the reducer directly. Marking these
				// untrusted would gain nothing (their types aren't guarded routes
				// either) while risking a false allowlist-miss silently breaking the
				// engine's resume/continuation flow.
				const handler = getHyperCardEffectHandler(e.name);
				const token = e.token;
				const sid = activeStackId;
				const resolveEmpty = () => {
					if (token) {
						dispatch({
							type: "ClassicyAppHyperCardResolveCommand",
							stackId: sid,
							token,
							result: "",
						});
					}
				};
				if (handler) {
					Promise.resolve(
						handler(e.args, {
							stackId: sid,
							resolve: (value: string) => {
								if (token) {
									dispatch({
										type: "ClassicyAppHyperCardResolveCommand",
										stackId: sid,
										token,
										result: value,
									});
								}
							},
							setField: (partId: string, value: string) =>
								dispatch({
									type: "ClassicyAppHyperCardCommitField",
									stackId: sid,
									partId,
									value,
								}),
							setVariable: (name: string, value) =>
								dispatch({
									type: "ClassicyAppHyperCardSetVariable",
									stackId: sid,
									name,
									value,
								}),
						}),
					).catch((err) => {
						classicyLog(
							"error",
							"HyperCard",
							"effect handler error",
							e.name,
							err,
						);
						resolveEmpty();
					});
				} else {
					// No handler for a blocking command → resolve empty so the stack
					// never hangs waiting on a plugin that isn't registered.
					resolveEmpty();
				}
			}
		}
		dispatch({
			type: "ClassicyAppHyperCardConsumeEffects",
			stackId: activeStackId,
			ids: consumed,
		});
	}, [open, activeStackId, pendingEffects, player, dispatch]);

	// --- drive `wait` timers ---
	useEffect(() => {
		if (!activeStackId || !waitToken) return;
		const timer = setTimeout(() => {
			dispatch({
				type: "ClassicyAppHyperCardWaitComplete",
				stackId: activeStackId,
				token: waitToken,
			});
		}, waitMs ?? 0);
		return () => clearTimeout(timer);
	}, [activeStackId, waitToken, waitMs, dispatch]);

	// --- load queued `.stack` documents (Finder OpenFile routing) ---
	// Double-clicking a Stack file appends its path to this app's
	// data.openFiles (the kernel's generic *OpenFile handling). Read it raw —
	// not through useHyperCardData — because on first open the kernel writes
	// { openFiles } before any HyperCard action has seeded openStacks. This
	// effect resolves each entry through the file system, fetches/decodes its
	// JSON, validates it, and either opens the stack (keyed by its path) or
	// reports the failure; both outcomes consume the path.
	const fs = useClassicyFileSystem();
	const rawOpenFiles = useAppManager((s) => {
		const d = s.System.Manager.Applications.apps[appId]?.data as
			| { openFiles?: unknown }
			| undefined;
		return Array.isArray(d?.openFiles) ? (d.openFiles as string[]) : undefined;
	});
	const openStacksById = data?.openStacks;
	const loadingRef = useRef<Set<string>>(new Set());
	useEffect(() => {
		if (!rawOpenFiles || rawOpenFiles.length === 0) return;
		for (const path of rawOpenFiles) {
			if (loadingRef.current.has(path)) continue;
			if (openStacksById?.[path]) {
				// Already open from this path (e.g. re-double-clicked): focus it
				// via the reducer's OpenFile case and clear the queue entry.
				dispatch({ type: "ClassicyAppHyperCardOpenFile", path });
				dispatch({ type: "ClassicyAppHyperCardOpenFileConsumed", path });
				continue;
			}
			loadingRef.current.add(path);
			const fileName = path.split(":").pop() ?? path;
			const fail = (message: string) => {
				loadingRef.current.delete(path);
				dispatch({
					type: "ClassicyAppHyperCardOpenFileFailed",
					path,
					message,
				});
			};
			let source: ReturnType<typeof resolveFileSystemEntrySource>;
			try {
				source = resolveFileSystemEntrySource(fs.resolve(path));
			} catch {
				source = { kind: "none" };
			}
			const textPromise: Promise<string> =
				source.kind === "url"
					? fetch(source.url).then((r) => {
							if (!r.ok) throw new Error(`HTTP ${r.status}`);
							return r.text();
						})
					: source.kind === "data"
						? decompressFromBase64(source.data).then((bytes) =>
								new TextDecoder().decode(bytes),
							)
						: Promise.reject(new Error("the file has no content"));
			textPromise
				.then((text) => {
					const result = validateStack(JSON.parse(text));
					if (result.ok === false) {
						fail(
							`“${fileName}” is not a valid HyperCard stack: ${result.errors[0]}`,
						);
						return;
					}
					loadingRef.current.delete(path);
					dispatch({
						type: "ClassicyAppHyperCardOpenStack",
						stackId: path,
						stackSource: path,
						stack: result.stack,
					});
					dispatch({ type: "ClassicyAppHyperCardOpenFileConsumed", path });
				})
				.catch((err: unknown) => {
					fail(
						`“${fileName}” could not be opened: ${err instanceof Error ? err.message : String(err)}.`,
					);
				});
		}
	}, [rawOpenFiles, openStacksById, fs, dispatch]);

	const openStack = useCallback(
		(id: string, stack: HCStack) => {
			dispatch({
				type: "ClassicyAppHyperCardOpenStack",
				stackId: id,
				stack,
			});
		},
		[dispatch],
	);

	// File → New Stack…: open a blank stack and land directly in edit mode —
	// a blank stack isn't useful in browse/run mode.
	const newStack = useCallback(() => {
		const openStacks = (
			useAppManager.getState().System.Manager.Applications.apps[appId]
				?.data as HyperCardData | undefined
		)?.openStacks;
		const id = generateUntitledStackId(openStacks);
		dispatch({
			type: "ClassicyAppHyperCardOpenStack",
			stackId: id,
			stack: makeBlankStack(),
		});
		dispatch({ type: "ClassicyAppHCEditEnter", stackId: id });
	}, [dispatch]);

	// Stacks a host app registered via registerHyperCardStack — surfaced in
	// the top-level Examples menu, not File (the bundled sample stacks were
	// removed from File entirely; see HyperCard.editor.test.tsx).
	const registeredStacks = useMemo(() => getRegisteredStacks(), []);

	const navigate = useCallback(
		(to: string) => {
			if (!activeStackId) return;
			if (editingActive) {
				dispatch({
					type: "ClassicyAppHCEditSetCard",
					stackId: activeStackId,
					to,
				});
				return;
			}
			dispatch({
				type: "ClassicyAppHyperCardNavigate",
				stackId: activeStackId,
				to,
			});
		},
		[activeStackId, editingActive, dispatch],
	);

	const appMenu: ClassicyMenuItem[] = useMemo(() => {
		// A View-menu toggle for one utility palette. Enabled whenever the palette
		// is mounted; checked while mounted and not hidden. Reads the live `closed`
		// flag so the checkmark tracks the palette's own title-bar close box.
		const paletteToggle = (
			windowId: string,
			title: string,
			shortcut: string,
			closed: boolean,
			size: [number, number],
			position: [number, number],
		): ClassicyMenuItem => ({
			id: `view_${windowId}`,
			title,
			keyboardShortcut: shortcut,
			disabled: !palettesMounted,
			checked: palettesMounted && !closed,
			onClickFunc: () => {
				if (!edit || !activeStackId) return;
				dispatch(
					closed
						? {
								type: "ClassicyWindowOpen",
								app: { id: appId },
								window: {
									id: windowId,
									size,
									position,
									minimumSize: [0, 0],
									windowType: "utility",
								},
							}
						: {
								type: "ClassicyWindowClose",
								app: { id: appId },
								window: { id: windowId },
							},
				);
			},
		});

		return [
			{
				id: "file",
				title: "File",
				menuChildren: [
					{ ...aboutMenuItem, title: `About ${appName}` },
					{ id: "file_sep_about", title: "-" },
					{
						id: "new_stack",
						title: "New Stack…",
						onClickFunc: newStack,
					},
					{ id: "file_sep", title: "-" },
					...(activeStackId && !edit
						? [
								{
									id: "edit_stack",
									title: "Edit Stack",
									onClickFunc: () =>
										dispatch({
											type: "ClassicyAppHCEditEnter",
											stackId: activeStackId,
										}),
								},
							]
						: []),
					...(activeStackId && edit
						? getHyperCardSaveProviders()
								.filter((p) => p.canSave())
								.map((provider) => ({
									id: `save_${provider.id}`,
									title:
										provider.id === "download"
											? "Save a Copy…"
											: `Save to ${provider.label}`,
									onClickFunc: () => {
										void provider
											.save(edit.draft, { stackId: activeStackId })
											.then((result) => {
												if ("error" in result) {
													dispatch({
														type: "ClassicyAppHyperCardOpenFileFailed",
														path: "",
														message: `The stack can’t be saved: ${result.error}`,
													});
												} else {
													dispatch({
														type: "ClassicyAppHCEditMarkSaved",
														stackId: activeStackId,
													});
													if (result.ref) {
														const newStackId = `saved:${provider.id}:${result.ref.id}`;
														if (newStackId !== activeStackId) {
															dispatch({
																type: "ClassicyAppHCEditRebindStack",
																stackId: activeStackId,
																newStackId,
															});
														}
													}
												}
											})
											.catch((err) => {
												dispatch({
													type: "ClassicyAppHyperCardOpenFileFailed",
													path: "",
													message: `The stack can’t be saved: ${err instanceof Error ? err.message : String(err)}`,
												});
											});
									},
								}))
						: []),
					...(activeStackId && edit
						? [
								{
									id: "save_stack",
									title: "Save Stack…",
									onClickFunc: () => setSaveStackOpen(true),
								},
							]
						: []),
					{
						id: "open_saved",
						title: "Open Saved Stack…",
						onClickFunc: () => setSavedStacksOpen(true),
					},
					// #236: a non-destructive Browse/Edit toggle — mirrors the toolbar's
					// Browse/Pointer tool buttons (ClassicyAppHCEditSetTool), which only
					// flip edit.tool and never touch the edit session (draft, dirty,
					// undo/redo). This item used to dispatch ClassicyAppHCEditExit,
					// which discards the whole session; that's still available for a
					// real discard-and-exit path, just no longer bound here.
					...(activeStackId && edit
						? [
								editingActive
									? {
											id: "toggle_edit_mode",
											title: "Browse",
											onClickFunc: () =>
												dispatch({
													type: "ClassicyAppHCEditSetTool",
													stackId: activeStackId,
													tool: "browse",
												}),
										}
									: {
											id: "toggle_edit_mode",
											title: "Edit",
											onClickFunc: () =>
												dispatch({
													type: "ClassicyAppHCEditSetTool",
													stackId: activeStackId,
													tool: "pointer",
												}),
										},
							]
						: []),
					...(activeStackId ? [{ id: "file_sep_2", title: "-" }] : []),
					// #236: Quit walks every dirty stack (Save/Discard/Cancel) before
					// actually dispatching ClassicyAppClose — see handleQuit above.
					{
						...quitMenuItemHelper(appId, appName, appIcon),
						onClickFunc: handleQuit,
					},
				],
			},
			...(activeStackId && edit
				? [
						{
							id: "edit",
							title: "Edit",
							menuChildren: [
								{
									id: "undo",
									title: "Undo",
									onClickFunc: () =>
										dispatch({
											type: "ClassicyAppHCEditUndo",
											stackId: activeStackId,
										}),
								},
								{
									id: "redo",
									title: "Redo",
									onClickFunc: () =>
										dispatch({
											type: "ClassicyAppHCEditRedo",
											stackId: activeStackId,
										}),
								},
								{ id: "edit_sep", title: "-" },
								{
									id: "copy_part",
									title: "Copy Part",
									onClickFunc: () =>
										edit.selectedPartId &&
										dispatch({
											type: "ClassicyAppHCEditCopyPart",
											stackId: activeStackId,
											partId: edit.selectedPartId,
										}),
								},
								{
									id: "paste_part",
									title: "Paste Part",
									onClickFunc: () =>
										dispatch({
											type: "ClassicyAppHCEditPastePart",
											stackId: activeStackId,
										}),
								},
								{
									id: "delete_part",
									title: "Delete Part",
									onClickFunc: () =>
										edit.selectedPartId &&
										dispatch({
											type: "ClassicyAppHCEditDeletePart",
											stackId: activeStackId,
											partId: edit.selectedPartId,
										}),
								},
								{ id: "edit_sep_2", title: "-" },
								{
									id: "edit_script",
									title: "Edit Script…",
									onClickFunc: () =>
										dispatch({
											type: "ClassicyAppHCEditShowScript",
											stackId: activeStackId,
											target: edit.selectedPartId
												? { kind: "part", partId: edit.selectedPartId }
												: { kind: "card" },
										}),
								},
							],
						},
					]
				: []),
			{
				id: "view",
				title: "View",
				menuChildren: [
					paletteToggle(
						"hypercard_tools",
						"Tools",
						"Ctrl+T",
						toolsClosed,
						[130, 0],
						[8, 100],
					),
					paletteToggle(
						"hypercard_inspector",
						"Info",
						"Ctrl+I",
						infoClosed,
						[240, 0],
						[8, 360],
					),
				],
			},
			{
				id: "go",
				title: "Go",
				menuChildren: [
					{
						id: "go_first",
						title: "First",
						onClickFunc: () => navigate("first"),
					},
					{ id: "go_prev", title: "Prev", onClickFunc: () => navigate("prev") },
					{ id: "go_next", title: "Next", onClickFunc: () => navigate("next") },
					{ id: "go_last", title: "Last", onClickFunc: () => navigate("last") },
					{ id: "go_sep", title: "-" },
					{ id: "go_back", title: "Back", onClickFunc: () => navigate("back") },
				],
			},
			...(activeStackId && edit
				? [
						{
							id: "objects",
							title: "Objects",
							menuChildren: [
								{
									id: "new_card",
									title: "New Card",
									onClickFunc: () =>
										dispatch({
											type: "ClassicyAppHCEditAddCard",
											stackId: activeStackId,
										}),
								},
								{
									id: "delete_card",
									title: "Delete Card",
									onClickFunc: () =>
										dispatch({
											type: "ClassicyAppHCEditDeleteCard",
											stackId: activeStackId,
										}),
								},
								{ id: "objects_sep", title: "-" },
								{
									id: "toggle_layer",
									title:
										edit.layer === "background"
											? "Edit Card Layer"
											: "Edit Background",
									onClickFunc: () =>
										dispatch({
											type: "ClassicyAppHCEditSetLayer",
											stackId: activeStackId,
											layer:
												edit.layer === "background" ? "card" : "background",
										}),
								},
							],
						},
					]
				: []),
			// A separate top-level menu (not a File submenu) for host-registered
			// stacks -- positioned after Objects while editing (Objects only
			// exists in edit mode, so this is simply the array's last entry,
			// and falls after Go in browse mode). Omitted entirely when no
			// host has registered a stack, so it never shows as an empty menu.
			...(registeredStacks.length > 0
				? [
						{
							id: "examples",
							title: "Examples",
							menuChildren: registeredStacks.map((entry) => ({
								id: `open_${entry.id}`,
								title: `Open “${entry.name}”`,
								onClickFunc: () => openStack(entry.id, entry.stack),
							})),
						},
					]
				: []),
		];
	}, [
		navigate,
		openStack,
		newStack,
		registeredStacks,
		activeStackId,
		edit,
		editingActive,
		palettesMounted,
		toolsClosed,
		infoClosed,
		dispatch,
		aboutMenuItem,
		handleQuit,
	]);

	// Keep the menu bar in sync with the dynamic edit-mode menus. Focus
	// changes (app-level and between this app's own windows) restore the
	// focused window's registration-time menuBar record, which goes stale the
	// moment the menus change (enter/exit edit mode, layer toggle). Whenever
	// this app is focused but the desktop shows a different menu than ours,
	// push the live one. The reducer stores the dispatched array by reference,
	// so the identity check settles after one dispatch — no loop.
	const isFocusedApp = useAppManager(
		(s) => s.System.Manager.Applications.focusedAppId === appId,
	);
	const desktopMenu = useAppManager((s) => s.System.Manager.Desktop.appMenu);
	useEffect(() => {
		if (isFocusedApp && desktopMenu !== appMenu) {
			dispatch({ type: "ClassicyWindowMenu", menuBar: appMenu });
		}
	}, [isFocusedApp, desktopMenu, appMenu, dispatch]);

	const currentCard = open
		? getCard(open.stack, open.currentCardId)
		: undefined;
	const editCard = edit
		? edit.draft.cards.find((c) => c.id === edit.currentCardId)
		: undefined;
	const windowTitle = open
		? editingActive && edit
			? `${edit.draft.name}${editCard?.name ? ` — ${editCard.name}` : ""}${edit.dirty ? " •" : ""}`
			: `${open.stack.name}${currentCard?.name ? ` — ${currentCard.name}` : ""}${edit?.dirty ? " •" : ""}`
		: appName;

	return (
		<ClassicyApp
			id={appId}
			name={appName}
			icon={appIcon}
			showDesktopIcon={false}
			defaultWindow={"hypercard_main"}
			handlesFileTypes={[ClassicyFileSystemEntryFileType.Stack]}
			handlesOwnFiles={true}
		>
			<ClassicyWindow
				// Keyed on the active stack: a stack opened from a .stack file
				// loads after the window mounted at its empty-state size, and
				// ClassicyWindow reads its size once at mount. The OpenStack
				// reducer stamps the fitted size onto the window record; the
				// remount picks it up.
				key={activeStackId ?? "no-stack"}
				id={"hypercard_main"}
				title={windowTitle}
				appId={appId}
				appMenu={appMenu}
				zoomable={false}
				scrollable={true}
				resizable={true}
				initialSize={open ? [(open.stack.size?.[0] ?? 420) + 4, 0] : [420, 320]}
				initialPosition={["center", 80]}
				onBeforeClose={onBeforeCloseMain}
			>
				{open && activeStackId ? (
					editingActive && edit ? (
						<div className={"classicyHyperCardStage"}>
							<HyperCardEditorCanvas stackId={activeStackId} edit={edit} />
						</div>
					) : (
						<div className={"classicyHyperCardStage"}>
							<HyperCardTransition
								transition={runtime?.transition}
								stackId={activeStackId}
								cardKey={open.currentCardId}
							>
								<HyperCardCard open={open} stackId={activeStackId} />
							</HyperCardTransition>
						</div>
					)
				) : (
					<div style={{ padding: "1em" }}>
						<ClassicyControlLabel
							label={"No stack open. Choose File → Open to load a stack."}
						/>
					</div>
				)}
			</ClassicyWindow>

			{edit && activeStackId ? (
				<ClassicyWindow
					id={"hypercard_tools"}
					title={"Tools"}
					appId={appId}
					appMenu={appMenu}
					zoomable={false}
					windowType={"utility"}
					initialSize={[130, 0]}
					initialPosition={[8, 100]}
				>
					<HyperCardToolsPalette stackId={activeStackId} edit={edit} />
				</ClassicyWindow>
			) : null}

			{edit && activeStackId ? (
				<ClassicyWindow
					id={"hypercard_inspector"}
					title={"Info"}
					appId={appId}
					appMenu={appMenu}
					windowType={"utility"}
					zoomable={false}
					initialSize={[240, 0]}
					initialPosition={[8, 360]}
				>
					<HyperCardInspector stackId={activeStackId} edit={edit} />
				</ClassicyWindow>
			) : null}

			{edit?.script && activeStackId ? (
				<ClassicyWindow
					id={"hypercard_script"}
					title={"Script"}
					appId={appId}
					appMenu={appMenu}
					initialSize={[440, 0]}
					initialPosition={["center", 120]}
					scrollable={true}
				>
					<HyperCardScriptEditor stackId={activeStackId} edit={edit} />
				</ClassicyWindow>
			) : null}

			<ClassicyFileOpenDialog
				id={"hypercard_open_stack"}
				appId={appId}
				open={savedStacksOpen}
				title={"Open Stack"}
				volumes={[desktopVolume(fs)]}
				fileTypeFilters={[
					{
						label: "HyperCard Stacks",
						types: [ClassicyFileSystemEntryFileType.Stack],
					},
					{ label: "All Documents", types: null },
				]}
				onOpenFunc={(selections) => {
					// The selected path feeds the same queue Finder uses: the
					// openFiles effect resolves, fetches, and validates it, then
					// dispatches OpenStack or the error dialog.
					for (const selection of selections) {
						dispatch({
							type: "ClassicyAppHyperCardOpenFile",
							path: String(
								selection.entry.meta?.classicyPath ?? selection.entry.id,
							),
						});
					}
					setSavedStacksOpen(false);
				}}
				onCancelFunc={() => setSavedStacksOpen(false)}
			/>

			{edit && activeStackId ? (
				<ClassicyFileSaveDialog
					id={"hypercard_save_stack"}
					appId={appId}
					open={saveStackOpen}
					title={"Save Stack"}
					volumes={[desktopVolume(fs)]}
					defaultFileName={edit.draft.name || "Untitled"}
					formats={[
						{
							label: "HyperCard Stack",
							extension: ".stack",
							fileType: ClassicyFileSystemEntryFileType.Stack,
							data: () => serializeStack(edit.draft),
						},
					]}
					onSaveFunc={() => {
						dispatch({
							type: "ClassicyAppHCEditMarkSaved",
							stackId: activeStackId,
						});
						setSaveStackOpen(false);
					}}
					onCancelFunc={() => setSaveStackOpen(false)}
				/>
			) : null}

			{open && activeStackId && runtime?.dialog ? (
				<HyperCardDialog dialog={runtime.dialog} stackId={activeStackId} />
			) : null}

			{closeGuardStackId
				? (() => {
						// Narrow to a stable non-null id for the closures below — this
						// IIFE just gives TypeScript a local const to capture.
						const guardStackId = closeGuardStackId;
						return (
							<ClassicyAlert
								id={"hypercard_close_guard"}
								appId={appId}
								alertType={"caution"}
								title={"Save changes?"}
								label={`Save changes to “${closeGuardEdit?.draft.name ?? "this stack"}” before closing?`}
								message={"If you don’t save, your changes will be lost."}
								defaultButtonId={"save"}
								buttons={[
									{
										id: "cancel",
										label: "Cancel",
										role: "cancel",
										onClick: () => resolveCloseGuard(false),
									},
									{
										id: "discard",
										label: "Don’t Save",
										// Discarding here really does mean discard: exit the
										// edit session and restore the stack's pristine copy
										// (the same ClassicyAppHCEditExit the old "Stop Editing
										// (Discard)" menu item used to fire unconditionally).
										// The difference is this only runs after the user
										// explicitly confirmed losing the changes.
										onClick: () => {
											dispatch({
												type: "ClassicyAppHCEditExit",
												stackId: guardStackId,
											});
											resolveCloseGuard(true);
										},
									},
									{
										id: "save",
										label: "Save",
										role: "default",
										onClick: () => saveDirtyStack(guardStackId),
									},
								]}
							/>
						);
					})()
				: null}

			{aboutWindow}
		</ClassicyApp>
	);
};

export default HyperCard;
